import { useEffect, useRef, useState } from 'react';

import { XIcon } from 'lucide-react';

import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { cn } from '@/new/lib/utils';

const normalizeAliases = (aliases: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of aliases) {
    const trimmed = raw.trim();
    if (trimmed === '') continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
};

const aliasesEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

export const EditAliasesDialog = ({
  open,
  onOpenChange,
  entityName,
  currentAliases,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  currentAliases: string[];
  onSave: (aliases: string[]) => Promise<void>;
}) => {
  const [aliases, setAliases] = useState<string[]>(currentAliases);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [shakingIndex, setShakingIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setAliases(currentAliases);
      setDraft('');
      setIsSaving(false);
    }
  }, [open, currentAliases]);

  const addDraftAlias = () => {
    const trimmed = draft.trim();
    if (trimmed === '') return;
    const existingIndex = aliases.findIndex((a) => a.toLowerCase() === trimmed.toLowerCase());
    if (existingIndex >= 0) {
      setShakingIndex(existingIndex);
      setTimeout(() => setShakingIndex(null), 400);
    } else {
      setAliases((prev) => [...prev, trimmed]);
    }
    setDraft('');
    inputRef.current?.focus();
  };

  const removeAlias = (index: number) => {
    setAliases((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const finalAliases = normalizeAliases(draft.trim() === '' ? aliases : [...aliases, draft]);
    try {
      setIsSaving(true);
      await onSave(finalAliases);
      onOpenChange(false);
    } catch {
      // Keep dialog open on failure so the user can retry.
    } finally {
      setIsSaving(false);
    }
  };

  const pendingAliases = normalizeAliases(draft.trim() === '' ? aliases : [...aliases, draft]);
  const hasChanges = !aliasesEqual(pendingAliases, currentAliases);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Aliases</DialogTitle>
          <DialogDescription>
            Add or remove alternative names for <strong>{entityName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {aliases.length === 0 && <p className="text-xs text-gray-500 italic">No aliases yet. Add one below.</p>}
            {aliases.map((alias, index) => (
              <Badge
                key={`${alias}-${index}`}
                variant="outline"
                className={cn(
                  'group flex cursor-pointer items-center gap-1 rounded-md bg-gray-50 duration-150 hover:border-red-300 hover:bg-red-50 hover:text-red-500',
                  shakingIndex === index && 'animate-shake border-red-300 bg-red-50 text-red-600',
                )}
                onClick={() => !isSaving && removeAlias(index)}
                aria-label={`Remove alias ${alias}`}
              >
                {alias}
                <XIcon className="text-muted-foreground group-hover:text-destructive -mr-0.75 size-2" />
              </Badge>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              className="h-8 py-1 text-xs text-gray-800"
              value={draft}
              placeholder="Add an alias..."
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDraftAlias();
                }
              }}
              disabled={isSaving}
            />
            <Button variant="outline" size="sm" onClick={addDraftAlias} disabled={isSaving || draft.trim() === ''}>
              Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} disabled={!hasChanges || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
